import React, { useState, useCallback } from 'react'
import {
  FormikConfig,
  FormikContext,
  FormikHelpers,
  FormikValues,
  useFormik,
  useFormikContext,
} from 'formik'
import { withStaticProperties, isWeb } from 'tamagui'

import { FormField } from './FormField'
import { ErrorText } from './FormField'

import { FormCurrencyInput } from './FormCurrencyInput.web'
import { FormImageUpload } from './FormImageUpload'
import { FormSelect } from './FormSelect'
import { FormSubmitButton } from './FormSubmitButton'
import { FormSwitch } from './FormSwitch'
import { FormTextInput } from './FormTextInput'
import { FormCheckbox } from './FormCheckbox'
import { FormRadioGroup } from './FormRadioGroup'
import { FormTimezonePicker } from './FormTimezonePicker'

const FormComponent = <V extends FormikValues>({
  children,
  initialValues,
  validationSchema,
  onSubmit,
  ...formikProps
}: { initialValues: V; children: React.ReactNode } & FormikConfig<V>) => {
  const [isSubmissionError, setIsSubmissionError] = useState(false)

  const formOnSubmit = useCallback(
    async (values: V, formikHelpers: FormikHelpers<any>) => {
      formikHelpers.setStatus(undefined)
      try {
        await onSubmit(values, formikHelpers)
        setIsSubmissionError(false)
      } catch (error) {
        setIsSubmissionError(true)
        formikHelpers.setStatus(
          error instanceof Error ? error.message : 'An error occurred'
        )
        throw error
      }
    },
    [onSubmit]
  )

  const formik = useFormik({
    ...formikProps,
    initialValues,
    validationSchema,
    onSubmit: formOnSubmit,
  })

  // Fallback keyDown handler for non-TextInput elements (e.g. Tamagui checkboxes/switches
  // rendered as <button>). react-native-web's TextInput calls e.stopPropagation() on ALL
  // keyDown events, so this handler never fires for TextInputs — Enter submission for those
  // relies on the browser's native implicit form submission (see FormTextInput.tsx).
  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.key === 'Enter') {
        const target = e.target as HTMLElement
        const tagName = target.tagName?.toLowerCase()

        // Don't submit from textareas (Enter inserts newlines) or selects (Enter interacts with dropdown)
        if (tagName === 'textarea' || tagName === 'select') return

        // Don't submit from contenteditable elements (e.g. Lexical rich text editor — Enter inserts newlines)
        if (target.isContentEditable) return

        // Don't submit from regular buttons — they have their own click handlers (e.g. submit button)
        // But DO submit from checkbox/radio/switch roles (Tamagui renders these as <button>)
        if (tagName === 'button') {
          const role = target.getAttribute('role')
          if (role !== 'checkbox' && role !== 'radio' && role !== 'switch')
            return
        }

        e.preventDefault()
        formik.handleSubmit()
      }
    },
    [formik]
  )

  return (
    // <ErrorBoundary fallback={<FormError />}>
    <FormikContext.Provider value={formik}>
      {isWeb ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!formik.isSubmitting) {
              formik.handleSubmit()
            }
          }}
          onKeyDown={handleFormKeyDown}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {children}
        </form>
      ) : (
        children
      )}
    </FormikContext.Provider>
    // </ErrorBoundary>
  )
}

const FormStatus = () => {
  const { status } = useFormikContext()

  if (!status) {
    return null
  }

  return <ErrorText size='$5'>{status}</ErrorText>
}

export const Form = withStaticProperties(FormComponent, {
  Field: FormField,

  CurrencyInput: FormCurrencyInput,
  ImageUpload: FormImageUpload,
  Select: FormSelect,
  SubmitButton: FormSubmitButton,
  Switch: FormSwitch,
  TextInput: FormTextInput,
  Checkbox: FormCheckbox,
  RadioGroup: FormRadioGroup,
  TimezonePicker: FormTimezonePicker,
  Status: FormStatus,
})
